import { Box, Text } from '@chakra-ui/react';
import React from 'react';

import { ACCORDION_NAME, accordionNameToIndex } from '../../../../components/LEAccordion/utils';
import Modal from '../../../../components/Modal';
import PrimaryButton from '../../../../components/ui/buttons/PrimaryButton';
import { OtherLienholder } from '../../../../constants/lienholders';
import {
  TemporaryInfo,
  useCancelPayoffRequestByTemporaryInfoIdMutation,
  useTemporaryInfoUpdateMutation,
} from '../../../../gql/generated/graphql';
import { useTempInfo } from '../../../../hooks/useTempInfo';

export const PAYOFF_CLEAN_DATA = {
  vehicle_payoff: null,
  automatically_fetched_payoff: false,
  good_through_date: null,
  payoff_includes_sales_tax: null,
  user_entered_total_payoff: null,
  payoff_initiated_time: null,
  payoff_duration_time_ms: null,
  sales_tax_from_payoff: null,
  sales_tax_from_payoff_entered_manually: null,
  stress_test_payoff_requested: null,
};

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  handleSubmit: () => void;
}

export const checkLienholderDifference = (
  info: TemporaryInfo,
  lienholder: string,
  other_lienholder: string,
) => {
  if (!info.data.lienholder) return false;
  return (
    lienholder !== info.data.lienholder ||
    (lienholder === OtherLienholder && other_lienholder !== info.data.other_lienholder)
  );
};

const ChangedLienholderModal = ({ isOpen, closeModal, handleSubmit }: Props) => {
  const { info } = useTempInfo({ fetchPolicy: 'network-only', refresh: isOpen });
  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();
  const [cancelPayoffRequestByTemporaryInfoId] = useCancelPayoffRequestByTemporaryInfoIdMutation();

  const clearLienholder = async () => {
    const [, currentPanelIndex] = info?.data?.current_accordion_panel?.split(':') ?? [];
    await updateTemporaryInfo({
      variables: {
        info: {
          id: info?.id,
          data: {
            ...PAYOFF_CLEAN_DATA,
            current_accordion_panel: `${accordionNameToIndex[ACCORDION_NAME.VEHICLE_ACCORDION]}:${
              currentPanelIndex || 0
            }`,
          },
        },
      },
    });
  };

  const cancelPayoffRequest = async () => {
    if (!info?.id) return;
    await cancelPayoffRequestByTemporaryInfoId({
      variables: {
        temporaryInfoId: info?.id,
      },
    });
  };

  const changeMyLienholder = async () => {
    await Promise.all([clearLienholder(), cancelPayoffRequest()]);
    handleSubmit();
  };

  return (
    <Modal
      onClose={closeModal}
      isOpen={isOpen}
      title={<>Heads Up!</>}
      size={{ base: 'md', md: '3xl' }}
    >
      <Box textAlign="center">
        <Text color="leaseEndBlue" mb={6} textAlign="center" fontSize={{ base: 'lg', md: 'xl' }}>
          If you change your lienholder, any payoff or good through date information you had saved
          will be deleted. That's because every lienholder has different directions for obtaining
          these values.
          <br />
          <br />
          If you have any questions about the process, please contact us for help.
        </Text>
        <PrimaryButton mt={4} onClick={changeMyLienholder}>
          CHANGE MY LIENHOLDER
        </PrimaryButton>
      </Box>
    </Modal>
  );
};

export default ChangedLienholderModal;
